import React, { useState, useEffect, Fragment } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Table, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { Trash2, Plus, Save } from "react-feather";

const LabCategoryEntry = ({ getLabItemsHandler, toggle, editData }) => {
  const auth = useSelector((state) => state.authentication);
  const itemsList = useSelector((state) => state.itemsList);
  const { itemsData } = itemsList;
  const { userData, branchData } = auth;
  const [errors, setErrors] = useState([]);

  const [category, setCategory] = useState([
    {
      categoryName: "",
      tests: [
        {
          testName: "",
          specimen: "",
          referenceRange: "",
          units: "",
          method: "",
          notes: "",
          subTests: [
            {
              subTestName: "",
              specimen: "",
              referenceRange: "",
              units: "",
              method: "",
              notes: "",
            },
          ],
        },
      ],
      createdBy: userData.user,
    },
  ]);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (editData) {
      setCategory([editData]);
    }
  }, [editData]);

  const handleCategoryChange = (categoryIndex, field, value) => {
    setCategory((prevCategory) => {
      const newCategory = [...prevCategory];
      newCategory[categoryIndex] = {
        ...newCategory[categoryIndex],
        [field]: value,
      };
      return newCategory;
    });
  };

  const handleTestChange = (categoryIndex, testIndex, field, value) => {
    setCategory((prevCategory) => {
      const newCategory = prevCategory.map((cat, index) => {
        if (index !== categoryIndex) {
          return cat;
        }

        const newTests = (cat.tests || []).map((test, tIndex) => {
          if (tIndex !== testIndex) {
            return test;
          }
          return { ...test, [field]: value };
        });

        return { ...cat, tests: newTests };
      });

      return newCategory;
    });
  };

  const handleSubTestChange = (
    categoryIndex,
    testIndex,
    subTestIndex,
    field,
    value
  ) => {
    setCategory((prevCategory) => {
      const newCategory = prevCategory.map((cat, cIndex) => {
        if (cIndex !== categoryIndex) {
          return cat;
        }

        const newTests = (cat.tests || []).map((test, tIndex) => {
          if (tIndex !== testIndex) {
            return test;
          }

          const updatedSubTests = (test.subTests || []).map(
            (subTest, sIndex) => {
              if (sIndex !== subTestIndex) {
                return subTest;
              }
              return { ...subTest, [field]: value };
            }
          );

          return { ...test, subTests: updatedSubTests };
        });

        return { ...cat, tests: newTests };
      });

      return newCategory;
    });
  };

  const addRow = () => {
    setCategory([
      ...category,
      {
        categoryName: "",
        createdBy: userData.user,
        tests: [],
      },
    ]);
  };

  const addTestRow = (categoryIndex) => {
    setCategory((prevCategory) => {
      const newCategory = prevCategory.map((cat, index) => {
        if (index !== categoryIndex) {
          return cat;
        }
        return {
          ...cat,
          tests: [
            ...(cat.tests || []),
            {
              testName: "",
              specimen: "",
              referenceRange: "",
              units: "",
              method: "",
              notes: "",
              subTests: [],
            },
          ],
        };
      });
      return newCategory;
    });
  };

  const addSubTestRow = (categoryIndex, testIndex) => {
    setCategory((prevCategory) => {
      const newCategory = prevCategory.map((cat, cIndex) => {
        if (cIndex !== categoryIndex) {
          return cat;
        }

        const newTests = (cat.tests || []).map((test, tIndex) => {
          if (tIndex !== testIndex) {
            return test;
          }

          const currentSubTests = test.subTests || [];

          const updatedSubTests = [
            ...currentSubTests,
            {
              subTestName: "",
              specimen: "",
              referenceRange: "",
              units: "",
              method: "",
              notes: "",
            },
          ];

          return { ...test, subTests: updatedSubTests };
        });

        return { ...cat, tests: newTests };
      });

      return newCategory;
    });
  };

  const removeCategory = (categoryIndex) => {
    setCategory((prevCategory) => {
      const newCategory = [...prevCategory];
      newCategory.splice(categoryIndex, 1);
      return newCategory;
    });
  };

  const removeTestRow = (categoryIndex, testIndex) => {
    setCategory((prevCategory) => {
      const newCategory = prevCategory.map((cat, index) => {
        if (index !== categoryIndex) {
          return cat;
        }
        return {
          ...cat,
          tests: cat.tests.filter((_, tIndex) => tIndex !== testIndex),
        };
      });
      return newCategory;
    });
  };

  const removeSubTestRow = (categoryIndex, testIndex, subTestIndex) => {
    setCategory((prevCategory) => {
      const newCategory = prevCategory.map((cat, cIndex) => {
        if (cIndex !== categoryIndex) {
          return cat;
        }

        const newTests = (cat.tests || []).map((test, tIndex) => {
          if (tIndex !== testIndex) {
            return test;
          }

          const updatedSubTests = (test.subTests || []).filter(
            (_, sIndex) => sIndex !== subTestIndex
          );

          return { ...test, subTests: updatedSubTests };
        });

        return { ...cat, tests: newTests };
      });

      return newCategory;
    });
  };

  const validateCategory = () => {
    const newErrors = [];

    category.forEach((category, categoryIndex) => {
      const categoryErrors = {};

      if (!category || !category.categoryName) {
        categoryErrors.categoryName = "Category Name is required";
      }

      if (category.tests) {
        category.tests.forEach((test, testIndex) => {
          if (!test || !test.testName) {
            categoryErrors[`test${testIndex}`] = "Test Name is required";
          }

          if (test.subTests) {
            test.subTests.forEach((subTest, subTestIndex) => {
              if (!subTest || !subTest.subTestName) {
                categoryErrors[`subTest${testIndex}_${subTestIndex}`] =
                  "SubTest Name is required";
              }
            });
          }
        });
      }

      if (Object.keys(categoryErrors).length > 0) {
        newErrors.push({
          categoryIndex,
          categoryErrors,
        });
      }
    });

    setErrors(newErrors);

    return newErrors.length === 0;
  };

  const submitHandler = () => {
    if (!validateCategory()) return;

    const apiUrl = editData
      ? `${process.env.REACT_APP_CRI_API}edit-lab-cat`
      : `${process.env.REACT_APP_CRI_API}add-lab-cat`;

    const requests = category.map((category) =>
      axios.post(apiUrl, category, {
        params: {
          branch: userData.branch,
          ...(editData && { id: category._id }),
        },
        headers: {
          Authorization: `Bearer ${userData.token}`,
        },
      })
    );

    axios
      .all(requests)
      .then((responses) => {
        responses.forEach(() => {
          getLabItemsHandler();
          toggle();
        });
      })
      .catch((error) => {
        console.error("Error:", error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          setServerError(error.response.data.message);
        } else {
          setServerError("An unexpected error occurred. Please try again.");
        }
      });
  };

  return (
    <div className="bg-white p-2 rounded">
      {serverError && (
        <Alert variant="danger" className="border-0 shadow-sm mb-4">
          {serverError}
        </Alert>
      )}

      <div className="table-responsive rounded-3 border border-light-subtle shadow-sm mb-4">
        <Table responsive bordered hover className="mb-0 align-middle">
          <thead className="table-dark text-nowrap small text-uppercase">
            <tr>
              <th className="py-3 px-3" style={{ width: "260px" }}>Category Config</th>
              <th className="py-3">Dynamic Test Matrix Mapping</th>
            </tr>
          </thead>
          <tbody>
            {category.map((cat, categoryIndex) => (
              <Fragment key={cat._id || categoryIndex}>
                <tr className="bg-light bg-opacity-40">
                  <td className="align-top px-3 py-4">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-secondary text-uppercase tracking-wider mb-2">Category Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={cat.categoryName}
                        placeholder="e.g., Biochemistry"
                        className="py-2 border-light-subtle shadow-sm"
                        onChange={(e) =>
                          handleCategoryChange(
                            categoryIndex,
                            "categoryName",
                            e.target.value
                          )
                        }
                      />
                    </Form.Group>
                    {errors.find((error) => error.categoryIndex === categoryIndex)
                      ?.categoryErrors?.categoryName && (
                      <div className="text-danger small mt-1 fw-medium">
                        {
                          errors.find(
                            (error) => error.categoryIndex === categoryIndex
                          ).categoryErrors.categoryName
                        }
                      </div>
                    )}
                    {category.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="mt-3 d-inline-flex align-items-center gap-1.5 px-2.5 rounded-2"
                        onClick={() => removeCategory(categoryIndex)}
                      >
                        <Trash2 size={13} /> Remove Category
                      </Button>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="rounded-2 overflow-hidden border border-light-subtle shadow-sm">
                      <Table responsive className="mb-0 align-middle table-sm-text">
                        <thead className="bg-secondary bg-opacity-10 text-secondary text-nowrap small text-uppercase fw-semibold border-bottom">
                          <tr>
                            <th className="py-2.5 px-2">Test Parameters Name</th>
                            <th className="py-2.5">Specimen</th>
                            <th className="py-2.5">Reference Range</th>
                            <th className="py-2.5">Units</th>
                            <th className="py-2.5">Method</th>
                            <th className="py-2.5">Notes</th>
                            <th className="py-2.5 text-center" style={{ width: "180px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(cat.tests || []).map((test, testIndex) => (
                            <Fragment key={test._id || testIndex}>
                              <tr className="border-bottom border-light-subtle">
                                <td className="p-2">
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Test Name"
                                    value={test.testName}
                                    onChange={(e) =>
                                      handleTestChange(
                                        categoryIndex,
                                        testIndex,
                                        "testName",
                                        e.target.value
                                      )
                                    }
                                  />
                                  {errors.find(
                                    (error) => error.categoryIndex === categoryIndex
                                  )?.categoryErrors?.[`test${testIndex}`] && (
                                    <div className="text-danger small mt-1 fw-medium">
                                      {
                                        errors.find(
                                          (error) =>
                                            error.categoryIndex === categoryIndex
                                        ).categoryErrors[`test${testIndex}`]
                                      }
                                    </div>
                                  )}
                                </td>
                                <td className="p-2">
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Specimen"
                                    value={test.specimen}
                                    onChange={(e) =>
                                      handleTestChange(
                                        categoryIndex,
                                        testIndex,
                                        "specimen",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td className="p-2">
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Reference Range"
                                    value={test.referenceRange}
                                    onChange={(e) =>
                                      handleTestChange(
                                        categoryIndex,
                                        testIndex,
                                        "referenceRange",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td className="p-2">
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Units"
                                    value={test.units}
                                    onChange={(e) =>
                                      handleTestChange(
                                        categoryIndex,
                                        testIndex,
                                        "units",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td className="p-2">
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Method"
                                    value={test.method}
                                    onChange={(e) =>
                                      handleTestChange(
                                        categoryIndex,
                                        testIndex,
                                        "method",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td className="p-2">
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Notes"
                                    value={test.notes}
                                    onChange={(e) =>
                                      handleTestChange(
                                        categoryIndex,
                                        testIndex,
                                        "notes",
                                        e.target.value
                                      )
                                    }
                                  />
                                </td>
                                <td className="p-2 text-center">
                                  <div className="d-flex justify-content-center gap-1.5">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      className="d-inline-flex align-items-center gap-1 px-2 rounded-2"
                                      onClick={() => addSubTestRow(categoryIndex, testIndex)}
                                    >
                                      <Plus size={12} /> SubTest
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      className="p-1.5 rounded-2 d-inline-flex align-items-center"
                                      onClick={() => removeTestRow(categoryIndex, testIndex)}
                                    >
                                      <Trash2 size={13} />
                                    </Button>
                                  </div>
                                </td>
                              </tr>

                              {/* Nested Sub-Tests Dynamic Rows Context */}
                              {test.subTests && test.subTests.length > 0 && (
                                <tr>
                                  <td colSpan="7" className="p-3 bg-light bg-opacity-25 border-bottom border-light-subtle">
                                    <div className="rounded-2 border border-light-subtle overflow-hidden shadow-xs bg-white ms-3">
                                      <Table responsive className="mb-0 align-middle">
                                        <thead className="bg-light text-muted small text-uppercase font-monospace border-bottom">
                                          <tr>
                                            <th className="py-2 px-2">↳ Sub-Test Name Parameter</th>
                                            <th className="py-2">Specimen</th>
                                            <th className="py-2">Reference Range</th>
                                            <th className="py-2">Units</th>
                                            <th className="py-2">Method</th>
                                            <th className="py-2">Notes</th>
                                            <th className="py-2 text-center" style={{ width: "80px" }}>Remove</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {test.subTests.map((subTest, subTestIndex) => (
                                            <tr key={subTest._id || subTestIndex} className="border-bottom-0">
                                              <td className="p-2">
                                                <Form.Control
                                                  type="text"
                                                  size="sm"
                                                  placeholder="SubTest Name"
                                                  value={subTest.subTestName}
                                                  onChange={(e) =>
                                                    handleSubTestChange(
                                                      categoryIndex,
                                                      testIndex,
                                                      subTestIndex,
                                                      "subTestName",
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                                {errors.find(
                                                  (error) => error.categoryIndex === categoryIndex
                                                )?.categoryErrors?.[`subTest${testIndex}_${subTestIndex}`] && (
                                                  <div className="text-danger small mt-1 fw-medium">
                                                    {
                                                      errors.find(
                                                        (error) =>
                                                          error.categoryIndex === categoryIndex
                                                      ).categoryErrors[`subTest${testIndex}_${subTestIndex}`]
                                                    }
                                                  </div>
                                                )}
                                              </td>
                                              <td className="p-2">
                                                <Form.Control
                                                  type="text"
                                                  size="sm"
                                                  placeholder="Specimen"
                                                  value={subTest.specimen}
                                                  onChange={(e) =>
                                                    handleSubTestChange(
                                                      categoryIndex,
                                                      testIndex,
                                                      subTestIndex,
                                                      "specimen",
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              </td>
                                              <td className="p-2">
                                                <Form.Control
                                                  type="text"
                                                  size="sm"
                                                  placeholder="Reference Range"
                                                  value={subTest.referenceRange}
                                                  onChange={(e) =>
                                                    handleSubTestChange(
                                                      categoryIndex,
                                                      testIndex,
                                                      subTestIndex,
                                                      "referenceRange",
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              </td>
                                              <td className="p-2">
                                                <Form.Control
                                                  type="text"
                                                  size="sm"
                                                  placeholder="Units"
                                                  value={subTest.units}
                                                  onChange={(e) =>
                                                    handleSubTestChange(
                                                      categoryIndex,
                                                      testIndex,
                                                      subTestIndex,
                                                      "units",
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              </td>
                                              <td className="p-2">
                                                <Form.Control
                                                  type="text"
                                                  size="sm"
                                                  placeholder="Method"
                                                  value={subTest.method}
                                                  onChange={(e) =>
                                                    handleSubTestChange(
                                                      categoryIndex,
                                                      testIndex,
                                                      subTestIndex,
                                                      "method",
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              </td>
                                              <td className="p-2">
                                                <Form.Control
                                                  type="text"
                                                  size="sm"
                                                  placeholder="Notes"
                                                  value={subTest.notes}
                                                  onChange={(e) =>
                                                    handleSubTestChange(
                                                      categoryIndex,
                                                      testIndex,
                                                      subTestIndex,
                                                      "notes",
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              </td>
                                              <td className="p-2 text-center">
                                                <Button
                                                  variant="outline-danger"
                                                  size="sm"
                                                  className="p-1.5 rounded-2 d-inline-flex align-items-center"
                                                  onClick={() =>
                                                    removeSubTestRow(
                                                      categoryIndex,
                                                      testIndex,
                                                      subTestIndex
                                                    )
                                                  }
                                                >
                                                  <Trash2 size={13} />
                                                </Button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mt-3 d-inline-flex align-items-center gap-1.5 px-3 rounded-2 shadow-sm bg-white"
                      onClick={() => addTestRow(categoryIndex)}
                    >
                      <Plus size={14} /> Add Test Parameter
                    </Button>
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Global Bottom Submission Controls */}
      <Row className="g-3 align-items-center border-top pt-3 mt-2">
        <Col className="d-flex gap-2">
          {!editData && (
            <Button 
              variant="outline-primary" 
              className="d-inline-flex align-items-center gap-1.5 px-3 py-2 shadow-sm bg-white"
              onClick={addRow}
            >
              <Plus size={16} /> Add Category Group
            </Button>
          )}
          <Button 
            variant="success" 
            className="d-inline-flex align-items-center gap-1.5 px-4 py-2 shadow-sm ms-auto"
            onClick={submitHandler}
          >
            <Save size={16} /> Save Configurations
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default LabCategoryEntry;